import React, { useEffect, useMemo, useState } from 'react';
import {
  AUTOMATED_PAYMENTS_MODAL_STATUS,
  AUTOMATION_TYPES,
  BASIC_FREQUENCY_OPTIONS,
} from '@/utils/constants/automations';
import dayjs from 'dayjs';

import ModalCommonButtonsContainer from '@/components/common-components/ModalComponent/ModalCommonButtonsContainer/ModalCommonButtonsContainer';
import ModalComponent from '@/components/common-components/ModalComponent/ModalComponent';

import '../../AutomationComponents/automationsInIIV.scss';

import {
  createAutomatedPayment,
  retryAutomatedPayment,
  updateAutomatedPayment,
} from '@/services/automatedPayment.service';
import { IAutomatedPayment } from '@Saral-Influencer-Marketing/saral-utils/src/types';
import clsx from 'clsx';
import { NotificationManager } from 'react-notifications';

import CalendarDates from '@/components/Icons/icons/CalendarDates';

import AmountInput from '../../AutomationComponents/AmountInput';
import AutomationHeader from '../../AutomationComponents/AutomationHeader';
import FrequencyInput from '../../AutomationComponents/FrequencyInput';
import PurposeInput from '../../AutomationComponents/PurposeInput';
import EndDatePicker from '../../ShipFreeProduct/AutomatedShipment/ModalComponents/EndDatePicker';
import PauseAutomationModal from '../../ShipFreeProduct/AutomatedShipment/ModalComponents/PauseAutomationModal';
import PaymentInfoTag from './PaymentInfoTag';

interface Props {
  mode: 'create' | 'edit';
  show: boolean;
  onClose: () => void;
  automatedPayment: IAutomatedPayment | null;
  profile_pic: string | null;
  influencerName: string | null;
  isRetry?: boolean;
  profileId: string;
}

const AutomatedPaymentsModal: React.FC<Props> = ({
  mode,
  show,
  onClose,
  automatedPayment,
  profile_pic,
  influencerName,
  isRetry = false,
  profileId,
}) => {
  const [status, setStatus] = useState('active');

  // state for create/update operations
  const [loading, setLoading] = useState(false);

  const [showPauseWarning, setShowPauseWarning] = useState(false);

  const [amount, setAmount] = useState<number | null>(null);
  const [selectedPurpose, setSelectedPurpose] = useState<string | null>(null);
  const [selectedFrequency, setSelectedFrequency] = useState<number | null>(30);
  const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);

  const handleCloseModal = () => {
    onClose();
    setShowPauseWarning(false);
    setSelectedFrequency(30);
    setSelectedEndDate(null);
    setSelectedPurpose(null);
    setAmount(null);
    setLoading(false);
  };

  const updatedFrequencyOptions = useMemo(() => {
    return BASIC_FREQUENCY_OPTIONS.map((option) => {
      if (!automatedPayment) return option;

      const date = dayjs(automatedPayment.nextPaymentScheduledAt)
        .subtract(automatedPayment.frequency, 'days')
        .add(option.value, 'days')
        .format('DD MMM, YYYY');

      return {
        ...option,
        isDisabled: dayjs(date).isBefore(dayjs()),
        disabledTooltip: 'Choose a frequency that aligns with the current schedule.',
      };
    });
  }, [BASIC_FREQUENCY_OPTIONS, automatedPayment]);

  const isCreateMode = mode === AUTOMATED_PAYMENTS_MODAL_STATUS.CREATE;

  const { isDisabled, tooltipText } = useMemo(() => {
    if (!amount || amount <= 0) {
      return { isDisabled: true, tooltipText: `Enter an amount to ${isCreateMode ? 'create' : 'save'} an automation` };
    }

    if (!selectedPurpose) {
      return { isDisabled: true, tooltipText: 'Select a purpose' };
    }

    if (!selectedEndDate && !isRetry) {
      return { isDisabled: true, tooltipText: 'Select an end date to save automation' };
    }

    return { isDisabled: false, tooltipText: '' };
  }, [amount, selectedEndDate, selectedPurpose, isCreateMode]);

  const showPaymentInfoTag = selectedFrequency && selectedEndDate && isCreateMode && amount && amount > 0;

  const handleSaveAutomatedPayment = async (statusFromPause?: string) => {
    setLoading(true);
    const body: any = {
      amount,
      purpose: selectedPurpose,
      frequency: Number(selectedFrequency),
      endDate: selectedEndDate,
    };

    if (mode === AUTOMATED_PAYMENTS_MODAL_STATUS.EDIT) {
      body.status = statusFromPause ? statusFromPause : status;
    } else {
      body.influencerId = profileId;
    }

    let response;
    if (mode === AUTOMATED_PAYMENTS_MODAL_STATUS.EDIT && automatedPayment) {
      response = await updateAutomatedPayment(body, automatedPayment._id);
    } else {
      response = await createAutomatedPayment(body);
    }
    if (response.status) {
      handleCloseModal();
      NotificationManager.success(
        statusFromPause === 'paused'
          ? 'Automation Paused!'
          : statusFromPause === 'active'
            ? 'Automation Activated!'
            : response?.message
      );
    } else {
      NotificationManager.error(response.data.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (automatedPayment && automatedPayment?._id) {
      setSelectedFrequency(automatedPayment.frequency || 30);
      setSelectedEndDate(automatedPayment.endDate ? automatedPayment.endDate.toString() : null);

      setStatus(automatedPayment.status);
      setAmount(automatedPayment.amount);
      setSelectedPurpose(automatedPayment.purpose);
    }
  }, [automatedPayment]);

  const handleRetryPayment = async () => {
    setLoading(true);

    const body = {
      paymentId: automatedPayment?._id,
      influencerId: profileId,
      purpose: selectedPurpose,
    };

    const response = await retryAutomatedPayment(body);

    if (response.status) {
      handleCloseModal();
      NotificationManager.success('Payment Successful!');
    } else {
      NotificationManager.error(response?.data?.message || 'Something went wrong');
    }

    setLoading(false);
  };

  return (
    <>
      {showPauseWarning ? (
        <PauseAutomationModal
          show={showPauseWarning}
          onClose={() => {
            setShowPauseWarning(false);
          }}
          setStatus={setStatus}
          handlePause={handleSaveAutomatedPayment}
          loading={loading}
          type={AUTOMATION_TYPES.PAYMENT}
        />
      ) : (
        <ModalComponent
          show={show}
          onClose={onClose}
          width={472}
          outsideClickClose={false}
          style={{ padding: 0 }}
          bodyStyle={{ padding: 0 }}>
          <AutomationHeader
            mode={isRetry ? AUTOMATED_PAYMENTS_MODAL_STATUS.EDIT : mode}
            status={status}
            setStatus={setStatus}
            setShowPauseWarning={setShowPauseWarning}
            showShadow={false}
            title="Automate Payment"
            showProfileChip
            profile_pic={profile_pic || ''}
            influencerName={influencerName || ''}
            showBottomBorder={false}
            retry={isRetry}
          />

          <div className="px-6">
            {selectedFrequency && !isCreateMode && automatedPayment && (
              <PaymentInfoTag
                currentDate={dayjs().toString()}
                endDate={selectedEndDate}
                frequency={selectedFrequency}
                mode={mode}
                amount={amount || 0}
                automatedPayment={automatedPayment}
                isRetry={isRetry}
              />
            )}
            <AmountInput
              label="Enter Amount"
              isRequired
              maxAmount={25000}
              amount={amount}
              setAmount={setAmount}
              disabled={isRetry}
            />
            <div className="my-4">
              <PurposeInput selectedPurpose={selectedPurpose} setSelectedPurpose={setSelectedPurpose} />
            </div>
            {!isRetry && (
              <>
                <div className="mt-4">
                  <FrequencyInput
                    label="Select Frequency"
                    required
                    options={updatedFrequencyOptions}
                    value={selectedFrequency?.toString() || ''}
                    onChange={(value) => {
                      setSelectedFrequency(Number(value));
                    }}
                    leftIcon={<CalendarDates />}
                    minCustomDelay={7}
                    minCustomDelayErrorMessage="Frequency can’t be less than 7 days"
                    maxCustomDelay={180}
                    maxCustomDelayErrorMessage="Frequency can’t be more than 180 days"
                  />
                  <span className="text-xs text-disabled">How often you want to send payment to the influencer .</span>
                </div>
                <div>
                  <EndDatePicker
                    selectedEndDate={selectedEndDate}
                    setSelectedEndDate={setSelectedEndDate}
                    frequency={Number(selectedFrequency)}
                    label="Send Payments Until"
                    className="mt-4"
                    placeholder="Select Date"
                  />
                </div>
              </>
            )}
            {showPaymentInfoTag && !isRetry ? (
              <PaymentInfoTag
                currentDate={dayjs().toString()}
                endDate={selectedEndDate}
                frequency={selectedFrequency}
                mode={mode}
                amount={amount || 0}
              />
            ) : (
              <></>
            )}
          </div>
          <div
            className={clsx('p-6', {
              'pt-0': showPaymentInfoTag,
              'pt-2': isRetry,
            })}>
            <ModalCommonButtonsContainer
              submitButtonText={isRetry ? 'Retry Payment' : isCreateMode ? 'Create Automation' : 'Save'}
              cancelButtonText={isRetry ? 'Close' : 'Cancel'}
              disabled={isDisabled}
              onSave={() => {
                if (isRetry) {
                  handleRetryPayment();
                } else {
                  handleSaveAutomatedPayment();
                }
              }}
              onClose={handleCloseModal}
              disabledTooltipText={tooltipText}
              loading={loading}
            />
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default AutomatedPaymentsModal;
